import { useReturnsQuery } from '@/queries/returns.query'
import type { Vault } from '@/types/vault'
import { cn } from '@/utils/cn'
import { formatPreciseTokenValue, formatUSD } from '@vaultsfyi/common/utils'
import type { Address } from 'viem'

interface TotalReturnsProps {
  walletAddress: Address | undefined
  vault: Pick<Vault, 'asset' | 'vaultId' | 'network' | 'childrenVaults'>
  containRewards?: boolean
  className?: string
  primaryValueClassName?: string
  secondaryValueClassName?: string
  subscriptionClassName?: string
  slashVisible?: boolean
  colorize?: boolean
  inlineRewards?: boolean
}

export function TotalReturns({
  vault,
  walletAddress,
  containRewards,
  className,
  primaryValueClassName,
  secondaryValueClassName,
  subscriptionClassName,
  slashVisible,
  colorize,
  inlineRewards,
}: TotalReturnsProps) {
  const { returnsNative, returnsUsd, isLoading } = useReturnsQuery({ address: walletAddress, vault })

  if (isLoading) {
    return (
      <div className={className}>
        <p className={primaryValueClassName}>
          <span className="animate-pulse duration-700">–</span>
          <span className="animate-pulse delay-300 duration-700">–</span>
        </p>
      </div>
    )
  }

  const colorClass = colorize
    ? returnsNative === undefined
      ? ''
      : returnsNative > 0n
        ? 'text-success'
        : returnsNative < 0n
          ? 'text-destructive'
          : ''
    : ''

  const hasData = returnsNative !== undefined && returnsUsd !== undefined

  return (
    <div className="flex flex-col gap-0.5">
      <div className={className}>
        <p className={cn(primaryValueClassName, colorClass)}>
          {returnsNative !== undefined ? (
            <>
              {formatPreciseTokenValue({ value: returnsNative, decimals: vault.asset.decimals })} {vault.asset.symbol}
            </>
          ) : returnsUsd === undefined ? (
            <span>––</span>
          ) : null}
        </p>
        {slashVisible && hasData && <p>/</p>}
        <p className={secondaryValueClassName}>
          {returnsUsd !== undefined && (
            <>
              {formatUSD(returnsUsd, { minimumFractionDigits: 2 })}
              {inlineRewards && returnsNative !== undefined && containRewards && (
                <span className={cn(subscriptionClassName, 'ml-1')}>+ rewards</span>
              )}
            </>
          )}
        </p>
      </div>
      {!inlineRewards && hasData && containRewards && <p className={cn(subscriptionClassName)}>+ rewards</p>}
    </div>
  )
}
